import { Avatar, AvatarV2, BadgeV3, DataGrid, TooltipV2 } from '@cimplico/components';
import { Link } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { Entity } from '../../types/entity';
import { calcRelativeTime, formatDateTime, formatMixedCase, parseTitle } from '../../utilities/formatting';
import { ArchiveBoxIcon } from '@heroicons/react/24/solid';
import { useRef } from 'react';
import useIsClamped from '../../hooks/use-line-clamp-active';
import EntityMetadata from './EntityMetadata';
import PendingDeactivationBadge, { ActiveBadge } from '../entity-overview/BillingStatusBadge';

type EntityRowProps = {
    id: string;
    entity: Entity;
    showSmallColumns: boolean;
};

function EntityRow({ id, entity, showSmallColumns }: EntityRowProps): JSX.Element {
    const { shortId } = useFirmContext();

    const entityNameRef = useRef(null);
    const legalNameRef = useRef(null);
    const isEntityNameClamped = useIsClamped(entityNameRef);
    const isLegalNameClamped = useIsClamped(legalNameRef);

    return (
        <DataGrid.Row id={entity.id}>
            <div className="flex flex-row items-center py-2 gap-2">
                {!showSmallColumns && (
                    <AvatarV2 alt={`${parseTitle(entity.name)}`} size="sm" rounded={false} color={entity.avatarHexColour} />
                )}{' '}
                <div className="flex flex-col gap-1">
                    <div className="w-full">
                        <div className="flex items-center relative w-fit">
                            <Link
                                to={`/${shortId}/entities/${entity.id}`}
                                className="line-clamp-1 leading-4 text-indigo-600 dark:text-indigo-400 hover:underline mr-2 text-sm break-all font-medium"
                                ref={entityNameRef}
                            >
                                <TooltipV2 disabled={!isEntityNameClamped} message={entity.name} delayMs={750}>
                                    <span className="w-fit block">{entity.name}</span>
                                </TooltipV2>
                            </Link>

                            <div className="whitespace-nowrap flex items-center gap-2">
                                {entity.subscriptionActive && !entity.flaggedDeactivatedAt && <ActiveBadge />}
                                {entity.subscriptionActive && entity.flaggedDeactivatedAt && <PendingDeactivationBadge />}
                                {entity.isArchived && (
                                    <TooltipV2 message="This entity is archived.">
                                        <ArchiveBoxIcon className="h-4 w-4 text-gray-500" />
                                    </TooltipV2>
                                )}
                            </div>
                        </div>
                        {entity.name !== entity.legalName && (
                            <Link
                                ref={legalNameRef}
                                to={`/${shortId}/entities/${entity.id}`}
                                className="w-full block mt-1 line-clamp-1 leading-4 text-gray-500 dark:text-gray-400 hover:underline text-sm break-all"
                            >
                                <span className="w-fit block">
                                    <TooltipV2
                                        message={isLegalNameClamped ? `Legal name: ${entity.legalName}` : 'Legal Name'}
                                        delayMs={750}
                                    >
                                        ({entity.legalName})
                                    </TooltipV2>
                                </span>
                            </Link>
                        )}
                    </div>

                    {showSmallColumns ? <EntityMetadata entity={entity} /> : undefined}
                </div>
            </div>

            {!showSmallColumns ? <p className="text-sm text-gray-600 dark:text-gray-400">{formatMixedCase(entity.type)}</p> : undefined}

            {!showSmallColumns ? <EntityGroup groups={entity.groups} /> : undefined}

            <TooltipV2 message={formatDateTime(entity.updatedAt)}>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{calcRelativeTime(entity.updatedAt)}</p>
            </TooltipV2>

            {!showSmallColumns ? (
                <Link to={`/${shortId}/entities/${entity.id}?tab=workpapers`}>
                    <p className="text-sm text-gray-600 dark:text-gray-400 hover:underline">{entity.activeWorkpapers.length}</p>
                </Link>
            ) : undefined}

            {!showSmallColumns ? <EntityIntegrations entity={entity} /> : undefined}
        </DataGrid.Row>
    );
}

export default EntityRow;

type EntityIntegrationsProps = {
    entity: Entity;
    showSmallColumns?: boolean;
};

export function EntityIntegrations({ entity, showSmallColumns }: EntityIntegrationsProps): JSX.Element {
    const integrations = (Object.keys(entity.integrations) as Array<keyof typeof entity.integrations>).filter(
        (key) => entity.integrations[key] !== null
    );

    const { shortId } = useFirmContext();

    return (
        <>
            {integrations.length > 0 ? (
                <Link to={`/${shortId}/entities/${entity.id}?tab=integrations`}>
                    <TooltipV2 message={integrations.map((i) => i.toUpperCase())}>
                        <p className={`${showSmallColumns ? 'text-xs' : 'text-sm'} text-gray-600 dark:text-gray-400 hover:underline`}>
                            {integrations.length}
                        </p>
                    </TooltipV2>
                </Link>
            ) : (
                <p className={`${showSmallColumns ? 'text-xs' : 'text-sm'} text-gray-600 dark:text-gray-400`}>-</p>
            )}
        </>
    );
}

type EntityGroupProps = {
    groups: { name: string; id: string }[];
    showSmallColumns?: boolean;
};

export function EntityGroup({ groups, showSmallColumns }: EntityGroupProps): JSX.Element {
    const { shortId } = useFirmContext();

    return (
        <div className={`${showSmallColumns ? 'text-xs' : 'text-sm'} font-medium`}>
            {groups[0] !== undefined ? (
                <Link to={`/${shortId}/groups/${groups[0]?.id}?tab=entities`}>
                    <p className="text-indigo-600 dark:text-indigo-400 hover:underline">{groups[0]?.name || '-'}</p>
                </Link>
            ) : (
                <p className="text-gray-600 dark:text-gray-400">-</p>
            )}
        </div>
    );
}

type StatusIconsProps = {
    subscriptionActive: boolean;
    isArchived: boolean;
};
function StatusIcons({ subscriptionActive, isArchived }: StatusIconsProps): JSX.Element {
    return (
        <div className="whitespace-nowrap">
            {subscriptionActive && <BadgeV3 size="small" color="indigo" label="Active" />}
            {isArchived && (
                <TooltipV2 message="This entity is archived.">
                    <ArchiveBoxIcon className="h-4 w-4 text-gray-500" />
                </TooltipV2>
            )}
        </div>
    );
}
